import { Box, Skeleton, type SxProps, type Theme, Typography } from "@mui/material";
import type { FC, ReactNode } from "react";
import { FACT_TILE, FONT_SIZE, FONT_WEIGHT, ICON_SIZE, INK, SHADOW, SURFACE } from "@/theme/tokens";
import MetaChip from "../Badges/MetaChip";
import { TrendChip, type TrendChipProps } from "./TrendChip";

/** How the fact changed against a baseline. */
export type FactTileDelta = Omit<TrendChipProps, "dataTestId">;

/** A figure that IS shown but isn't the whole truth ("Partly estimated"); the chip names it and `reason` says why. */
export type FactTileQualifier = {
	label: string;
	reason: string;
	// DEFAULT: absent = a text-only chip.
	icon?: ReactNode;
};

/** The fact can't be stated (withheld, hidden, not computable). Say WHY: an absence without a reason reads as a bug. */
export type FactTileAbsence = {
	/** Already-translated short statement ("Not shown"). */
	label: string;
	reason: string;
	// DEFAULT: absent = no glyph beside the dash.
	icon?: ReactNode;
};

type ValueProps = {
	/** The fact itself ("Thu, Oct 8"), pre-formatted. */
	value: ReactNode;
	// DEFAULT: absent = no comparison to report.
	delta?: FactTileDelta;
	// DEFAULT: absent = a clean figure.
	qualifier?: FactTileQualifier;
	/** A muted line under the value ("1 accepted · 11 declined"). */
	// DEFAULT: absent = the value alone.
	caption?: ReactNode;
	absence?: never;
	loading?: never;
};

/** Still fetching: the value is skeletoned, and nothing is claimed yet about whether it will turn out absent. */
type LoadingProps = { loading: true; value?: never; delta?: never; qualifier?: never; caption?: never; absence?: never };

/** No value, so no change, qualifier or caption either: the reason takes the caption's line. */
type AbsentProps = { absence: FactTileAbsence; value?: never; delta?: never; qualifier?: never; caption?: never; loading?: never };

type Props = (ValueProps | LoadingProps | AbsentProps) & {
	/** Leading glyph before the label; pass a `fontSize="inherit"` icon. */
	icon: ReactNode;
	/** What the fact is ("Date"). Already translated. */
	label: string;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

const testId = (dataTestId: string | undefined, part: string): string | undefined => (dataTestId === undefined ? undefined : `${dataTestId}-${part}`);

const CAPTION_SX = { fontSize: FONT_SIZE.CAPTION, color: INK.SECONDARY } as const;

const AbsentValue: FC<{ absence: FactTileAbsence; dataTestId?: string }> = ({ absence, dataTestId }) => (
	<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }} data-testid={testId(dataTestId, "absent")}>
		{absence.icon !== undefined && (
			<Box component="span" aria-hidden sx={{ display: "inline-flex", color: INK.DISABLED, fontSize: ICON_SIZE.INLINE }}>
				{absence.icon}
			</Box>
		)}
		<Box component="span" aria-hidden sx={{ color: INK.DISABLED }}>
			{"—"}
		</Box>
		<Box component="span" sx={{ fontSize: FONT_SIZE.EMPHASIS, color: INK.MUTED }}>
			{absence.label}
		</Box>
	</Box>
);

/** The value row: a skeleton, the absence, or the figure with its change and qualifier chips. */
const FactValue: FC<{ tile: Props }> = ({ tile }) => {
	if (tile.absence !== undefined) return <AbsentValue absence={tile.absence} dataTestId={tile.dataTestId} />;
	if (tile.loading === true) return <Skeleton variant="text" width={FACT_TILE.SKELETON_VALUE_WIDTH} data-testid={testId(tile.dataTestId, "skeleton")} />;
	return (
		<>
			{/* Its own flex row, so a value made of parts (a glyph and a time) keeps the gap it always had. */}
			<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, minWidth: 0 }} data-testid={testId(tile.dataTestId, "value")}>
				{tile.value}
			</Box>
			{tile.delta !== undefined && <TrendChip {...tile.delta} dataTestId={testId(tile.dataTestId, "delta")} />}
			{tile.qualifier !== undefined && (
				<MetaChip label={tile.qualifier.label} icon={tile.qualifier.icon} tone="warning" dataTestId={testId(tile.dataTestId, "qualifier")} />
			)}
		</>
	);
};

/**
 * **One fact about the subject** — a date, a headcount, a cost — as a small raised card: brand glyph and label, the
 * value, and an optional caption. Lay several in a flex row (each `flex: 1`). For a measure against a baseline add a
 * `delta` chip; for a figure that is shown but partial a `qualifier`; for one that can't be shown at all an `absence`
 * — never `value={0}`. Read-only; for a figure that filters a list use `ClickableStatTile`, for a strip of KPIs `StatStrip`.
 */
export const FactTile: FC<Props> = (props) => {
	// Not destructured: the value / absence union only narrows on the props object itself.
	const { icon, label, dataTestId, sx } = props;
	const footnote = props.absence === undefined ? props.caption : props.absence.reason;
	const qualifierReason = props.qualifier?.reason;
	return (
		<Box
			data-testid={dataTestId}
			data-absent={props.absence === undefined ? undefined : "true"}
			aria-busy={props.loading === true ? true : undefined}
			sx={[
				{
					flex: "1 1 0",
					minWidth: 0,
					minHeight: FACT_TILE.MIN_HEIGHT,
					boxSizing: "border-box",
					px: 1.75,
					py: 1.5,
					borderRadius: 1,
					backgroundColor: SURFACE,
					boxShadow: SHADOW.PANEL_SOFT,
					display: "flex",
					flexDirection: "column",
					gap: 0.5,
				},
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			<Box
				component="span"
				sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, fontSize: FONT_SIZE.CAPTION, fontWeight: FONT_WEIGHT.MEDIUM, color: "primary.main" }}
			>
				<Box component="span" aria-hidden sx={{ display: "inline-flex", fontSize: FONT_SIZE.EMPHASIS }}>
					{icon}
				</Box>
				{label}
			</Box>
			<Typography
				component="span"
				sx={{
					display: "flex",
					alignItems: "center",
					flexWrap: "wrap",
					gap: 0.75,
					fontSize: FONT_SIZE.TITLE_SM,
					fontWeight: FONT_WEIGHT.SEMIBOLD,
					color: INK.PRIMARY,
					minWidth: 0,
				}}
			>
				<FactValue tile={props} />
			</Typography>
			{footnote !== undefined && (
				<Typography
					component="span"
					// The absence's reason wraps: cut to "visible to a…", the tile is back to an unexplained blank.
					sx={[CAPTION_SX, props.absence === undefined && { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }]}
					data-testid={testId(dataTestId, "caption")}
				>
					{footnote}
				</Typography>
			)}
			{qualifierReason !== undefined && (
				<Typography component="span" sx={CAPTION_SX} data-testid={testId(dataTestId, "qualifier-reason")}>
					{qualifierReason}
				</Typography>
			)}
		</Box>
	);
};
